// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PageContent } from '@/components/page-content';
import {
  Sort,
  SortDirection,
  invert as invertDirection,
  sortDirectionSchema,
} from '@/constants/sort';
import { ExchangeOrderListing } from '@/exchange-order/components/exchange-order-listing';
import {
  ExchangeOrderSearchListing,
  ExchangeOrderSortProperty,
  exchangeOrderSearchListingSchema,
} from '@/exchange-order/hooks/use-exchange-orders';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Local storage key for persisting sort preferences.
 */
const LOCAL_STORAGE_SORT_KEY = 'exchange-order-sort';

/**
 * Default sort property when no preference is set.
 */
const DEFAULT_SORT_PROPERTY: ExchangeOrderSortProperty = 'eoNumber';

/**
 * Default sort direction when no preference is set.
 */
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Schema for validating sort preferences.
 */
const sortSchema = z.object({
  property: z.enum(['eoNumber', 'status']),
  direction: sortDirectionSchema,
});

/**
 * Route configuration for the exchange listing page.
 */
export const Route = createFileRoute('/_app/exchanges/')({
  component: ExchangeOrderRoute,
  validateSearch: exchangeOrderSearchListingSchema,
});

/**
 * Main component for the exchange listing page.
 */
function ExchangeOrderRoute() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  /**
   * Sort preference persistence in local storage.
   */
  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  /**
   * Current sort configuration.
   * Prioritizes URL parameters over local storage preferences.
   */
  const sort: Sort<ExchangeOrderSortProperty> = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  /**
   * Updates sort configuration.
   * Saves preference to local storage and updates URL.
   */
  const setSort = (
    property: ExchangeOrderSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({ search: (prev) => ({ ...prev, direction, sort: property }) });
  };

  /**
   * Handles column header click for sorting.
   * Toggles direction if same column, otherwise sorts ascending.
   */
  const handleSort = (property: ExchangeOrderSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invertDirection(sort.direction));
    }
    setSort(property, 'asc');
  };

  const searchParameters: ExchangeOrderSearchListing = {
    page: search.page,
    query: search.query,
    status: search.status,
    sort: search.sort,
    direction: search.direction,
  };

  return (
    <PageContent>
      <ExchangeOrderListing
        currentStatus={search.status ?? 'all'}
        searchParameters={searchParameters}
        sort={sort}
        onSort={handleSort}
        navigateFn={navigate}
      />
    </PageContent>
  );
}
